import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import { CardRow } from '@dapp/multi-lib/sdaoui/CardRow';
import { useTranslation } from 'react-i18next';
import { formatValueToFourDecimals } from '@dapp/multi-lib/lib/formatters';
import Link from 'next/link';
import { Body, Body1, Body3 } from 'components/Typography';
import { HorizontalLine } from '@dapp/multi-lib/sdaoui/HorizontalLine';
import { UsdValue } from 'components/UsdValue';
import { VaultPrice } from 'structure/pv2/dynavault/components/VaultPrice';
import { VaultPosition } from 'structure/pv2/dynavault/components/VaultPosition';
import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { cx } from 'class-variance-authority';
import { toNumber } from 'lodash';
import { VaultType } from 'structure/pv2/vault/configs/vaultTypes';
import {
  getRiskLevelColorClass,
  getRiskLevelLabel,
} from 'structure/pv2/vault/utils/vault';
import { useVaultOnChainData } from 'structure/pv2/dynavault/hooks/useVaultListBlockchain';
import { useConnection } from 'wagmi';
import { VaultAPY } from 'structure/pv2/vault/components/VaultAPY';
import { VaultStatus } from 'structure/pv2/dynavault/components/VaultStatus';
import { VaultDepositAssetIcon } from 'structure/pv2/dynavault/components/VaultDepositAssetIcon';
import { hasVaultPosition } from 'structure/pv2/dynavault/utils/vaultPosition';

type DynaVaultCardProps = {
  vault: DynaVault;
};

// const mapStatusToOutline: Record<Vault['status'], SurfaceProps['outline']> =
//   {
//     active: 'default',
//     closed: 'red',
//   };

export const VaultCard = (props: DynaVaultCardProps) => {
  const { vault } = props;
  const { t } = useTranslation('common');
  const connection = useConnection();

  const vaultOnChainData = useVaultOnChainData({
    vaultAddress: vault.address,
    chainId: vault.chainId,
    walletAddress: connection.address as string,
  });

  // Convert vault price (in deposit token) to USD using oracle
  const vaultPriceInUsd = vaultOnChainData.data
    ? Number(vaultOnChainData.data.vaultPrice)
    : 0;

  const tvl = vaultOnChainData.data ? vaultOnChainData.data.tvlUsd : '0';
  const hasPosition = hasVaultPosition(vaultOnChainData.data?.walletBalance);

  return (
    <Surface
      rounded='medium'
      style='gradient'
      className={cx(
        'flex w-[291px] flex-col p-5 lg:p-6',
        !hasPosition && 'border-[#616F8B66]/40'
      )}
      outline={hasPosition ? 'green' : 'default'}
    >
      <div className='flex flex-col'>
        <CardRow justify='start' className='gap-2'>
          <VaultDepositAssetIcon
            vault={vault}
            depositAssetSymbol={vaultOnChainData.data?.underlyingAssetSymbol}
          />
          <div className='flex flex-col'>
            <Body3 className='lowercase text-[#D9D9D9] first-letter:uppercase'>
              {vault.type}
            </Body3>
            <Body1 className='font-light text-[#F0F0F0]'>{vault.symbol}</Body1>
          </div>
        </CardRow>
        <div className='mb-7 mt-7 flex flex-col'>
          <CardRow className='h-12'>
            <RowLabel>{t('DYNAVAULT.CURRENT_PRICE')}</RowLabel>
            <VaultPrice
              price={vaultPriceInUsd}
              showPerformance={!vault.isClosed}
              performance={`${formatValueToFourDecimals(toNumber(vault.twentyFourHourPerformance))}%`}
            />
          </CardRow>
          <HLine />
          <CardRow className='h-12	'>
            <RowLabel>{t('DYNAVAULT.TOTAL_TVL')}</RowLabel>
            <UsdValue size='small' value={tvl} />
          </CardRow>
          <HLine />
          {/* <CardRow className='h-12	'>
            <RowLabel>{t('APR')}</RowLabel>
            <Body2>{formatPercent(parseFloat(vault.apr))}%</Body2>
          </CardRow> */}
          <HLine />
          <CardRow className='h-12'>
            <RowLabel>
              {vault.type == VaultType.YIELD
                ? t('ANNUAL_PERCENTAGE_YIELD')
                : t('SINCE_INCEPTION')}
            </RowLabel>
            <VaultAPY vault={vault} />
          </CardRow>
          <HLine />
          <CardRow className='h-12	'>
            <RowLabel>{t('DYNAVAULT.POSITION')}</RowLabel>
            <VaultPosition vault={vault} />
          </CardRow>
          <HLine />
          <CardRow className='h-12'>
            <RowLabel>{t('DYNAVAULT.RISK_LEVEL')}</RowLabel>
            <Body
              className={cx(
                'text-[0.75rem] leading-[1.125rem]',
                getRiskLevelColorClass(vault.riskLevel)
              )}
            >
              {getRiskLevelLabel(vault.riskLevel)}
            </Body>
          </CardRow>
        </div>
      </div>
      <div className='flex flex-row items-center justify-between'>
        <VaultStatus
          vault={vault}
          className='text-[0.75rem] leading-[1.125rem]'
        />
        <Button
          component={Link}
          size='small'
          href={`/vaults/${vault.address}:${vault.chainId}`}
          // style='ghost'
        >
          {t('DYNAVAULT.EXPLORE')}
        </Button>
      </div>
    </Surface>
  );
};

interface RowLabelProps {
  children: React.ReactNode;
}

const RowLabel = ({ children }: RowLabelProps) => {
  return (
    <Body className='text-[0.75rem] leading-[1.125rem] text-white-500'>
      {children}
    </Body>
  );
};

const HLine = () => {
  return <HorizontalLine className='border-[#262626]' />;
};
